import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {
  DataTable,
  DataTableConfig,
  DataTableDataKey,
  ImportedRow,
  ImportedRowUpdate,
  Row,
} from 'notebook/data-tables/DataTableConstants';
import {
  addRowToDataTable,
  convertDataTableToTable,
  createDataTable,
  generateDataTableRow,
} from 'notebook/data-tables/DataTableUtil';
import toColumnLabel from 'notebook/structured-tables/columns/toColumnLabel';
import excess from 'notebook/tables/excess';

function _updateRow(
  dataTableConfig: DataTableConfig,
  row: Row,
  cellUpdates: Array<{columnName: string; text: string}>
): Row {
  return {
    ...row,
    cells: row.cells.map((cell, j) => {
      const columnName = toColumnLabel({
        structuredTableColumn: dataTableConfig.columns[j],
        schemas: [dataTableConfig.assaySchema],
      });
      const cellUpdate = _.findWhere(cellUpdates, {columnName});
      if (!cellUpdate) {
        return cell;
      }
      return {...cell, text: cellUpdate.text, typedValue: null};
    }),
  };
}

function _generateRowFromImport(
  dataTableConfig: DataTableConfig,
  rowIndex: number,
  dataKey: DataTableDataKey,
  cellValues: Array<{columnName: string; text: string}>
): Row {
  const defaultRow = generateDataTableRow(dataTableConfig, rowIndex, dataKey);
  return _updateRow(dataTableConfig, defaultRow, cellValues);
}

/**
 * Immutably add rows to `dataTable` from `importedRows`, using column defaults if cell values are not
 * specified.
 *
 * @typedef {Object} ImportedRow
 * @property {Object} dataKey - ids of the row's linked item
 * @property {string} dataKey.stageRunId - id of stage run, if linking to a stage run sample
 * @property {string} dataKey.stageRunSampleId - id of sample, if linking to a stage run sample
 * @property {Object[]} cellValues - an array of values for cells in a given row
 * @property {string} cellValues[].columnName - the dataTable column name to match on
 * @property {string} cellValues[].text - the text to set on the matching cell
 *
 * @param {Object} dataTable - data table to immutably update
 * @param {ImportedRow[]} importedRows - rows to add into `dataTable`
 * @returns {Object} - updated data table
 */
export async function insertRowsFromSpreadsheet(
  dataTable: DataTable,
  importedRows: Array<ImportedRow>
): Promise<DataTable> {
  // Workaround to fix saving string resolution results (BNCH-10859).
  //
  // Strategy:
  // 1. Run string resolution in a separate evaluation context, so that the Redux store is pre-loaded
  //    with string resolution results.
  // 2. Wait for string resolution to finish
  // 3. Add or update data table rows. Strings will be resolved immediately and saved along with the entry.
  //    This works because the string resolution results are already pre-loaded in Redux,
  //    and we don't need to make a request to the server.
  //
  // Here, we generate dummy data table rows and then run string resolution in a separate evaluation context.
  //
  // These rows are identical to what will be added to the table, and they should be evaluated the same way,
  // with one exception: cross-table formulas will error because we're evaluating the table in isolation.
  //
  // Although cross-table formulas won't work, they shouldn't be a big problem.
  // String resolution results will still save properly unless the following combination of causes
  // happens all at once:
  // - The data table config has default values with cross-table formulas
  // - The cross-table formulas are in a link field and trigger string resolution
  // - The user never edits the entry after spreadsheet import.
  //
  // In the long term, BNCH-10859 will be fixed by treating string resolution as an entry edit,
  // and this workaround can be removed.

  // If Persist Evaluation is enabled, string resolution will already be invoked at time of snapshot creation.
  // Calling `_waitForStringResolution` would trigger another evaluation which would be redundant, and
  // more importantly, may not be in sync with the initial resolution query.
  if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER) {
    const dummyRowsForStringResolution = importedRows.map(({dataKey, cellValues}, rowIndex) =>
      _generateRowFromImport(dataTable.config, rowIndex, dataKey, cellValues)
    );
    await _waitForStringResolution(dataTable.config, dummyRowsForStringResolution);
  }

  let newDataTable = dataTable;
  for (const {dataKey, cellValues} of importedRows) {
    const newRowIndex = newDataTable.rows.length;
    let newRow = generateDataTableRow(dataTable.config, newRowIndex, dataKey);
    newRow = _updateRow(dataTable.config, newRow, cellValues);
    newDataTable = addRowToDataTable(newDataTable, newRow);
  }
  return newDataTable;
}

async function _waitForStringResolution(config: DataTableConfig, newRows: Array<Row>): Promise<void> {
  const dataTable = {...createDataTable(config, {noRows: true}), rows: newRows};
  const table = convertDataTableToTable(dataTable);
  await new excess.SpreadsheetEvaluator({
    fullTableMap: {[table.name]: table},
    selectors: [],
    parse: excess.parse,
  }).outputWithStringsResolved();
}

function _updateRowFromSpreadsheet(
  dataTable: DataTable,
  importedRows: Array<ImportedRowUpdate>,
  row: Row
): Row {
  const importedRow = _.findWhere(importedRows, {euid: row.euid});
  if (!importedRow) {
    return row;
  }
  return _updateRow(dataTable.config, row, importedRow.cellUpdates);
}

/**
 * Immutably update cell values in any rows in `dataTable` that are referenced in `importedRows`.
 *
 * @typedef {Object} ImportedRowUpdate
 * @property {string} euid - the euid of the dataTable row to update
 * @property {Object[]} cellUpdates - an array of updates for cells in a given row
 * @property {string} cellUpdates[].columnName - the dataTable column name to match on
 * @property {string} cellUpdates[].text - the text to set on the matching cell
 *
 * @param {Object} dataTable - data table to immutably update
 * @param {ImportedRow[]} importedRows - updates to merge into `dataTable`
 * @return {Object} - updated data table
 */
export async function updateRowsFromSpreadsheet(
  dataTable: DataTable,
  importedRows: Array<ImportedRowUpdate>
): Promise<DataTable> {
  // If Persist Evaluation is enabled, string resolution will already be invoked at time of snapshot creation.
  // Calling `_waitForStringResolution` would trigger another evaluation which would be redundant, and
  // more importantly, may not be in sync with the initial resolution query.
  if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER) {
    const dummyRowsForStringResolution = importedRows.map(({cellUpdates}, rowIndex) =>
      _generateRowFromImport(dataTable.config, rowIndex, {}, cellUpdates)
    );
    await _waitForStringResolution(dataTable.config, dummyRowsForStringResolution);
  }

  return {
    ...dataTable,
    rows: dataTable.rows.map((row) => _updateRowFromSpreadsheet(dataTable, importedRows, row)),
  };
}
